/**
 * CarouselGrid — responsive layout that becomes a horizontal-scroll
 * carousel on mobile and a normal CSS grid on tablet+.
 *
 * On mobile (<md) the children render as a left-padded, snap-x
 * horizontal rail. Each card is sized to ~78% viewport width so the
 * user sees the first card + a peek of the next, encouraging a
 * sideways swipe. We also render scroll-progress dots beneath the rail
 * with a "Swipe →" hint that fades out once the user starts scrolling
 * — both signal that the section continues sideways even on phones
 * where a single-card-peek alone is easy to miss.
 *
 * Static class strings are required for Tailwind's JIT scanner — do
 * not concat layout classes from runtime values.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';

const GRID_BY_VARIANT = {
  threeCol: 'md:grid md:grid-cols-2 lg:grid-cols-3',
  fourCol: 'md:grid md:grid-cols-2 lg:grid-cols-4',
  threeColTight: 'md:grid md:grid-cols-2 lg:grid-cols-3',
  reviews: 'md:grid md:grid-cols-3',
};

export default function CarouselGrid({
  children,
  variant = 'threeCol',
  className = '',
  'data-testid': testId,
}) {
  const items = React.Children.toArray(children).filter(Boolean);
  const gridClass = GRID_BY_VARIANT[variant] || GRID_BY_VARIANT.threeCol;
  const railRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);

  // Track which card is most-visible inside the rail so the dots reflect
  // the user's scroll position. Only matters on mobile.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    const onScroll = () => {
      if (!hasScrolled && rail.scrollLeft > 8) setHasScrolled(true);
      const railRect = rail.getBoundingClientRect();
      const centerX = railRect.left + railRect.width / 2;
      let bestIdx = 0;
      let bestDist = Infinity;
      Array.from(rail.children).forEach((child, idx) => {
        const r = child.getBoundingClientRect();
        const dist = Math.abs(r.left + r.width / 2 - centerX);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = idx;
        }
      });
      setActiveIndex(bestIdx);
    };
    rail.addEventListener('scroll', onScroll, { passive: true });
    return () => rail.removeEventListener('scroll', onScroll);
  }, [hasScrolled]);

  return (
    <div className={className}>
      <div className="relative">
        <div
          ref={railRef}
          className={`flex ${gridClass} gap-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none scrollbar-none -mx-4 px-4 md:mx-0 md:px-0 pb-4 md:pb-0`}
          style={{ scrollbarWidth: 'none' }}
          data-testid={testId}
        >
          {items.map((child, i) => (
            <div
              key={child.key ?? i}
              className="flex-shrink-0 md:flex-shrink-0 w-[78%] sm:w-[55%] md:w-auto snap-start"
            >
              {child}
            </div>
          ))}
        </div>

        {/* Right-edge fade — signals more content scrolls off-rail.
            Hidden on md+ where the grid is fully visible. Fades out as
            the user scrolls, so it doesn't linger at the end. */}
        <div
          aria-hidden
          className={`md:hidden pointer-events-none absolute right-0 top-0 bottom-4 w-12 bg-gradient-to-l from-rsc-slate to-transparent transition-opacity duration-300 ${
            hasScrolled ? 'opacity-0' : 'opacity-100'
          }`}
        />
      </div>

      {/* Scroll progress + swipe hint — mobile only. The pill nudges
          gently for the first few seconds via the bounce animation,
          then quiets down once the user has touched the rail. */}
      {items.length > 1 && (
        <div className="md:hidden mt-2 flex flex-col items-center gap-2">
          <div
            className="flex items-center gap-1.5"
            aria-label={`Card ${activeIndex + 1} of ${items.length}`}
          >
            {items.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === activeIndex
                    ? 'w-5 bg-rsc-brand-blue'
                    : 'w-1.5 bg-rsc-charcoal-muted/30'
                }`}
              />
            ))}
          </div>
          {!hasScrolled && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rsc-charcoal-muted animate-pulse">
              Swipe to see more
              <ChevronRight className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
      )}
    </div>
  );
}
